import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import { useNavigate } from "react-router-dom";
import ColumnSelection from "../../components/CustomComponents/ColumnSelection/ColumnSelection";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import Select from "react-select";
import DropDownStyles, {
  DropDownStylesDark,
  defaultSelectPortalProps,
} from "../staff/dropdownStyles";
import APIs from "../../Constants/apis";
import { useSelector, useDispatch } from "react-redux";
import swal from "sweetalert";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast";
import DateRangePicker from "../../components/CustomComponents/DateRangePicker/DateRangePicker";
import moment from "moment";
import axiosInstance from "../../config/axiosApi/axiosApi";
import { useProfileAndSettings } from "../../hooks/useProfileAndSettings";
import {
  setVisibleColumns,
  setFilterField,
} from "../../app/itemGridPreferencesSlice";
import { mergeVisibleColumns } from "../../utils/preferencesUtils";
import useResetPageOnFilterChange from "../../hooks/useResetPageOnFilterChange";
import useDebouncedEffect from "../../hooks/useDebouncedEffect";
import ErrorBoundary from "../../components/CustomComponents/ErrorBoundary/ErrorBoundary.jsx";
import ModernGridToolbar, {
  ModernGridToolbarFilterPill,
} from "../../components/CustomComponents/ModernGridToolbar/ModernGridToolbar";
import ModernGridPagerFooter from "../../components/CustomComponents/ModernGridToolbar/ModernGridPagerFooter";
import GridEmptyState from "../../components/CustomComponents/ModernGridToolbar/GridEmptyState";
import TableActionsMenu, {
  TableActionsItem,
} from "../../components/CustomComponents/TableActionsMenu/TableActionsMenu";
import OrderDetailsModal from "../../components/CustomComponents/OrderDetailsModal/OrderDetailsModal";
import { getModernGridFilterPillSelectStyles } from "../../components/CustomComponents/ModernGridToolbar/modernGridToolbarSelectStyles";

const ManageOrder = () => {
  const CompID = useSelector((state) => state.auth.user?.userData?.compID);
  const { getCompCurrency } = useProfileAndSettings();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;
  const filterPillSelectStyles = useMemo(
    () => getModernGridFilterPillSelectStyles(dropdownStyles, isDarkMode),
    [dropdownStyles, isDarkMode]
  );
  const themeColor = useThemeColor();

  const SHOW_BULK_ACTIONS = false;

  const [search, setSearch] = useState("");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [customerDetails, setCustomerDetails] = useState(null);
  const [detailedOrder, setDetailedOrder] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const compCurrency = getCompCurrency();
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [selectedOrders, setSelectedOrders] = useState(new Set());
  const lastSelectedIndex = useRef(null);
  const ordersFetchSeqRef = useRef(0);
  const pageKey = "orders";
  const defaultVisibleColumns = {
    frontorderid: true,
    orderType: true,
    orderStatus: true,
    orderSendBy: true,
    invoiceNo: true,
    source: true,
    tillname: false,
    customer: true,
    orderDate: true,
    paymentStatus: true,
    paymentMethod: true,
    totalAmount: true,
    fillBy: true,
  };
  const defaultType = { value: "", label: "All" };
  const defaultPaymentStatus = { value: "", label: "All" };
  const defaultOrderStatus = { value: "", label: "All" };
  // Default to "This Week" if no existing filter
  const defaultDateRange = [
    moment().startOf('week').toDate(),
    moment().endOf('week').toDate(),
  ];
  const defaultPageSize = { value: "50", label: "50" };
  const validPageSizes = ["20", "50", "100"];

  const preferences = useSelector(
    (state) => state.itemGridPreferences[pageKey] || {}
  );
  const visibleColumns = mergeVisibleColumns(
    preferences.visibleColumns,
    defaultVisibleColumns
  );
  const pageSize =
    preferences.pageSize &&
    validPageSizes.includes(String(preferences.pageSize.value))
      ? preferences.pageSize
      : defaultPageSize;

  const handlePageSizeChange = useCallback(
    (value) => {
      dispatch(setFilterField({ pageKey, field: "pageSize", value }));
    },
    [dispatch, pageKey]
  );


  const type = preferences.type || defaultType;
  const paymentStatus = preferences.paymentStatus || defaultPaymentStatus;
  const OrderStatus = preferences.OrderStatus || defaultOrderStatus;
  // Use existing dateRange from Redux if present, otherwise use "This Week" default
  const dateRange = preferences.dateRange || defaultDateRange;
  const [startDate, endDate] = dateRange;

  const currencySymbols = {
    GBP: "£",
    USD: "$",
    EUR: "€",
  };

  const getCurrencySymbol = (currencyCode) => {
    return currencySymbols[currencyCode] || currencyCode;
  };

  // Currency fetching now handled by useProfileAndSettings hook

  // Error boundary for ResizeObserver
  useEffect(() => {
    // Override ResizeObserver to suppress the error
    const OriginalResizeObserver = window.ResizeObserver;
    window.ResizeObserver = class extends OriginalResizeObserver {
      constructor(callback) {
        super((entries, observer) => {
          try {
            callback(entries, observer);
          } catch (error) {
            if (error.message && error.message.includes('ResizeObserver')) {
              // Suppress ResizeObserver errors
              return;
            }
            throw error;
          }
        });
      }
    };

    const handleResizeObserverError = (e) => {
      if (
        e.message ===
        "ResizeObserver loop completed with undelivered notifications."
      ) {
        e.stopImmediatePropagation();
        e.preventDefault();
        return false;
      }
    };

    const handleUnhandledRejection = (e) => {
      if (
        e.reason &&
        e.reason.message &&
        e.reason.message.includes("ResizeObserver")
      ) {
        e.preventDefault();
        return false;
      }
    };

    // Add multiple event listeners to catch the error at different levels
    window.addEventListener("error", handleResizeObserverError, true);
    window.addEventListener("unhandledrejection", handleUnhandledRejection, true);

    // Also override console.error to suppress ResizeObserver errors
    const originalConsoleError = console.error;
    console.error = (...args) => {
      if (args[0] && typeof args[0] === 'string' && args[0].includes('ResizeObserver')) {
        return; // Suppress ResizeObserver console errors
      }
      originalConsoleError.apply(console, args);
    };

    return () => {
      // Restore original ResizeObserver
      window.ResizeObserver = OriginalResizeObserver;
      window.removeEventListener("error", handleResizeObserverError, true);
      window.removeEventListener("unhandledrejection", handleUnhandledRejection, true);
      console.error = originalConsoleError;
    };
  }, []);

  // Cleanup effect to prevent memory leaks
  useEffect(() => {
    return () => {
      // Clear any pending timeouts
      const timeouts = [];
      const originalSetTimeout = window.setTimeout;
      window.setTimeout = (...args) => {
        const id = originalSetTimeout(...args);
        timeouts.push(id);
        return id;
      };

      // Clear all timeouts on unmount
      timeouts.forEach(id => clearTimeout(id));

      // Clear any pending API requests
      if (window.AbortController) {
        // This will be handled by axios interceptors if configured
      }

      // Restore body overflow on unmount
      document.body.style.overflow = 'unset';
    };
  }, []);

  useEffect(() => {
    if (!preferences.visibleColumns) {
      dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
    }
    if (!preferences.type) {
      dispatch(setFilterField({ pageKey, field: "type", value: defaultType }));
    }
    if (!preferences.paymentStatus) {
      dispatch(
        setFilterField({
          pageKey,
          field: "paymentStatus",
          value: defaultPaymentStatus,
        })
      );
    }
    if (!preferences.OrderStatus) {
      dispatch(
        setFilterField({
          pageKey,
          field: "OrderStatus",
          value: defaultOrderStatus,
        })
      );
    }
    if (!preferences.dateRange) {
      dispatch(
        setFilterField({ pageKey, field: "dateRange", value: defaultDateRange })
      );
    }
    if (
      !preferences.pageSize ||
      !validPageSizes.includes(String(preferences.pageSize?.value))
    ) {
      dispatch(setFilterField({ pageKey, field: "pageSize", value: defaultPageSize }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, pageKey]);

  const typeOptions = [
    { value: "", label: "All" },
    { value: "Delivery", label: "Delivery" },
    { value: "Collection", label: "Pickup" },
    { value: "Dine-In", label: "Dine In" },
  ];



  const paymentStatusOptions = [
    { value: "", label: "All" },
    { value: "Pending", label: "Unpaid" },
    { value: "Completed", label: "Paid" },
    { value: "Partial Refund", label: "Partial Refund" },
    { value: "Full Refund", label: "Full Refund" },
  ];



  const isFullRefundStatus = (s) => {
    if (!s || typeof s !== "string") return false;
    const n = s.trim().toLowerCase().replace(/[\s_-]/g, "");
    return n === "fullrefund" || n === "refund";
  };
  const isPartialRefundStatus = (s) => {
    if (!s || typeof s !== "string") return false;
    const n = s.trim().toLowerCase().replace(/[\s_-]/g, "");
    return n === "partialrefund";
  };

  const OrderStatusOptions = [
    { value: "", label: "All" },
    { value: "Draft", label: "Draft" },
    { value: "Pending", label: "Pending" },
    { value: "In Process", label: "In Process" },
    { value: "Ready", label: "Ready" },
    { value: "Served", label: "Served" },
    { value: "Completed", label: "Completed" },
    { value: "Cancelled", label: "Cancelled" },
    { value: "Full Refund", label: "Full Refund" },
    { value: "Partial Refund", label: "Partial Refund" },
    { value: "Rejected", label: "Rejected" },
    { value: "Open", label: "Open" },
  ];

  const fetchCustomerDetails = useCallback(
    async (customerKey) => {
      if (!customerKey || !CompID) {
        setCustomerDetails(null);
        return;
      }

      const isGuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(customerKey).trim());
      const endpoint = isGuid ? APIs.GET_CUSTOMER_BY_CUST_GUID : APIs.GET_CUSTOMER_BY_ID;
      const params = isGuid
        ? { CompID, Cust_Guid: String(customerKey).trim() }
        : { CompID, Cust_Id: customerKey };

      try {
        setLoading(true);
        const response = await axiosInstance.get(endpoint, {
          params,
        });

        const data = response.data.data || response.data;
        setCustomerDetails({
          Cust_Fname: data.cust_Fname || "",
          Cust_Lname: data.cust_Lname || "",
          Cust_Email: data.cust_Email || "",
          Cust_Mobile: data.cust_Mobile || "",
          Cust_Address: data.cust_Address || "",
        });
      } catch (error) {
        showErrorToast("Error fetching customer details");
        setCustomerDetails(null);
      } finally {
        setLoading(false);
      }
    },
    [CompID]
  );

  const fetchOrderByInvoiceNo = useCallback(
    async (invoiceNo) => {
      if (!invoiceNo || !CompID) return;

      try {
        setDetailsLoading(true);
        const response = await axiosInstance.get(APIs.GET_ORDER_BY_INVOICE_NO, {
          params: { CompID, InvoiceNo: invoiceNo },
        });

        if (response.data.status) {
          setDetailedOrder(response.data.data);
        } else {
          showErrorToast(
            response.data.message || "Failed to fetch order details"
          );
          setDetailedOrder(null);
        }
      } catch (error) {
        showErrorToast("Error fetching order details");
        setDetailedOrder(null);
      } finally {
        setDetailsLoading(false);
      }
    },
    [CompID]
  );

  const fetchOrdersData = useCallback(async () => {
    const fetchSeq = ++ordersFetchSeqRef.current;
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_ALL_ORDERS, {
        params: {
          CompID,
          Search: search?.trim() || "",
          PageSize: pageSize.value,
          PageNumber: pageNumber,
          Start: startDate && moment(startDate).isValid() ? moment(startDate).format("YYYY-MM-DD") : "",
          End: endDate && moment(endDate).isValid() ? moment(endDate).format("YYYY-MM-DD") : "",
          OrderType: type?.value || "",
          PaymentStatus: paymentStatus?.value || "",
          OrderStatus: OrderStatus?.value || "",
        },
      });

      if (fetchSeq !== ordersFetchSeqRef.current) return;

      const ordersData = response.data.data || response.data;
      const records = response.data.totalRecords || 0;

      setTotalPages(response.data.totalPages || 0);
      setTotalRecords(records);
      if (Array.isArray(ordersData)) {
        setOrders(ordersData);
      } else {
        setOrders([]);
      }

    } catch (err) {
      if (fetchSeq !== ordersFetchSeqRef.current) return;
      showErrorToast("Error fetching orders");
    } finally {
      if (fetchSeq === ordersFetchSeqRef.current) {
        setLoading(false);
        setInitialLoadComplete(true);
      }
    }
  }, [search, startDate, endDate, type, paymentStatus, OrderStatus, CompID, pageSize, pageNumber]);

  useResetPageOnFilterChange(setPageNumber, [
    search,
    type?.value,
    paymentStatus?.value,
    OrderStatus?.value,
    startDate,
    endDate,
    pageSize?.value,
  ]);

  useDebouncedEffect(() => fetchOrdersData(), [fetchOrdersData]);

  useEffect(() => {
    if (!initialLoadComplete) return;
    setLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    search,
    pageNumber,
    pageSize?.value,
    type?.value,
    paymentStatus?.value,
    OrderStatus?.value,
    startDate,
    endDate,
  ]);

  const handleDelete = async (order) => {
    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to delete Order #${order.recvorder_ID}?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      if (!CompID || !order.recvorder_ID) {
        showErrorToast("Invalid Company ID or Order ID");
        return;
      }

      setLoading(true);
      const response = await axiosInstance.delete(APIs.DELETE_ORDER, {
        params: {
          CompID,
          RecvOrder_ID: order.recvorder_ID,
        },
      });

      if (response.status === 200 && response.data.status) {
        setOrders((prevOrders) =>
          prevOrders.filter((item) => item.recvorder_ID !== order.recvorder_ID)
        );
        showSuccessToast("Order is Deleted.");
      } else {
        showErrorToast(response.data.message || "Deletion failed");
      }
    } catch (err) {
      showErrorToast(err.response?.data?.Message || "Something Went Wrong.");
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSearch("");
    dispatch(
      setFilterField({ pageKey, field: "dateRange", value: defaultDateRange })
    );
    dispatch(setFilterField({ pageKey, field: "type", value: defaultType }));
    dispatch(
      setFilterField({
        pageKey,
        field: "paymentStatus",
        value: defaultPaymentStatus,
      })
    );
    dispatch(
      setFilterField({
        pageKey,
        field: "OrderStatus",
        value: defaultOrderStatus,
      })
    );
  };

  const handleSelectOrder = (orderId, index, event) => {
    setSelectedOrders((prev) => {
      const newSelected = new Set(prev);

      if (event.shiftKey && lastSelectedIndex.current !== null) {
        const start = Math.min(lastSelectedIndex.current, index);
        const end = Math.max(lastSelectedIndex.current, index);

        for (let i = start; i <= end; i++) {
          if (orders[i]?.recvorder_ID) {
            newSelected.add(orders[i].recvorder_ID);
          }
        }
      } else {
        if (newSelected.has(orderId)) {
          newSelected.delete(orderId);
        } else {
          newSelected.add(orderId);
        }
        lastSelectedIndex.current = index;
      }

      return newSelected;
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const newSelected = new Set(orders.map((order) => order.recvorder_ID));
      setSelectedOrders(newSelected);
      lastSelectedIndex.current = null;
    } else {
      setSelectedOrders(new Set());
      lastSelectedIndex.current = null;
    }
  };

  const handleDeselectAll = () => {
    setSelectedOrders(new Set());
    lastSelectedIndex.current = null;
  };

  const handleBulkComplete = async () => {
    if (selectedOrders.size === 0) {
      showErrorToast("No orders selected for completion.");
      return;
    }

    try {
      const confirmComplete = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to mark ${selectedOrders.size} selected orders as completed?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmComplete) return;

      setLoading(true);
      await Promise.all(
        Array.from(selectedOrders).map((orderId) =>
          axiosInstance.post(APIs.POST_UPDATE_ORDER_STATUS, {
            CompID,
            RecvOrder_ID: orderId,
            OrderStatus: "Completed",
          })
        )
      );

      setSelectedOrders(new Set());
      lastSelectedIndex.current = null;
      fetchOrdersData();
      showSuccessToast(`${selectedOrders.size} orders marked as completed.`);
      setLoading(false);
    } catch (err) {
      showErrorToast("Failed to complete selected orders.");
      setLoading(false);
    }
  };

  const handleBulkPay = async () => {
    if (selectedOrders.size === 0) {
      showErrorToast("No orders selected for payment.");
      return;
    }

    try {
      const confirmPay = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to mark ${selectedOrders.size} selected orders as paid?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmPay) return;

      setLoading(true);
      await Promise.all(
        Array.from(selectedOrders).map((orderId) =>
          axiosInstance.post(APIs.POST_UPDATE_ORDER_PAYMENT, {
            CompID,
            RecvOrder_ID: orderId,
            PaymentStatus: "Paid",
          })
        )
      );

      setSelectedOrders(new Set());
      lastSelectedIndex.current = null;
      fetchOrdersData();
      showSuccessToast(`${selectedOrders.size} orders marked as paid.`);
      setLoading(false);
    } catch (err) {
      showErrorToast("Failed to update payment status for selected orders.");
      setLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedOrders.size === 0) {
      showErrorToast("No orders selected for deletion.");
      return;
    }

    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to delete ${selectedOrders.size} selected orders?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      setLoading(true);
      await Promise.all(
        Array.from(selectedOrders).map((orderId) =>
          axiosInstance.delete(APIs.DELETE_ORDER, {
            params: {
              CompID,
              RecvOrder_ID: orderId,
            },
          })
        )
      );

      setSelectedOrders(new Set());
      lastSelectedIndex.current = null;
      fetchOrdersData();
      showSuccessToast(`${selectedOrders.size} orders deleted successfully.`);
      setLoading(false);
    } catch (err) {
      showErrorToast("Failed to delete selected orders.");
      setLoading(false);
    }
  };

  const isDefaultDateRange = () => {
    if (!startDate || !endDate) return false;
    const defStart = moment(defaultDateRange[0]).startOf("day");
    const defEnd = moment(defaultDateRange[1]).endOf("day");
    return (
      moment(startDate).isSame(defStart, "day") &&
      moment(endDate).isSame(defEnd, "day")
    );
  };

  const hasFiltersApplied = () =>
    search.trim() !== "" ||
    type?.value !== "" ||
    paymentStatus?.value !== "" ||
    OrderStatus?.value !== "" ||
    !isDefaultDateRange();

  const showGridToolbar = initialLoadComplete;

  const getEmptyStateDescription = () => {
    const parts = [];
    if (search.trim()) {
      parts.push(`search "${search.trim()}"`);
    }
    if (type?.value) {
      parts.push(`type "${type.label}"`);
    }
    if (paymentStatus?.value) {
      parts.push(`payment "${paymentStatus.label}"`);
    }
    if (OrderStatus?.value) {
      parts.push(`status "${OrderStatus.label}"`);
    }
    if (!isDefaultDateRange()) {
      parts.push("the selected date range");
    }
    if (parts.length === 0) {
      return "There are no orders for the selected period. Try a different date range or check back later.";
    }
    return `No orders match your current ${parts.join(", ")}. Try different filters or clear them to see all orders.`;
  };

  const renderBulkSelectionActions = () => {
    if (!SHOW_BULK_ACTIONS || selectedOrders.size === 0) return null;

    return (
      <div className="mb-3">
        <div className="p-3 bg-light rounded d-flex align-items-center justify-content-between">
          <span className="fw-bold text-primary">
            {selectedOrders.size} order{selectedOrders.size > 1 ? "s" : ""} selected
          </span>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={handleDeselectAll}
          >
            Clear Selection
          </button>
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-sm btn-success"
              onClick={handleBulkComplete}
              title="Mark as Completed"
            >
              <i className="fa fa-check me-1" />
              Complete
            </button>
            <button
              type="button"
              className="btn btn-sm btn-info"
              onClick={handleBulkPay}
              title="Mark as Paid"
            >
              <i className="fa fa-credit-card me-1" />
              Mark Paid
            </button>
            <button
              type="button"
              className="btn btn-sm btn-danger"
              onClick={handleBulkDelete}
              title="Delete Selected Orders"
            >
              <i className="fa fa-trash me-1" />
              Delete
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderNoDataMessage = () => {
    if (loading || orders.length > 0) return null;
    return (
      <GridEmptyState
        isDarkMode={isDarkMode}
        themeColor={themeColor}
        title="No orders found"
        description={getEmptyStateDescription()}
        onClearFilters={hasFiltersApplied() ? clearFilters : undefined}
        clearFiltersLabel="Clear all filters"
      />
    );
  };

  const handleOpenModal = (order) => {
    setShowModal(true);
    setSelectedOrder(order);
    setCustomerDetails(null);
    setDetailedOrder(null);
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';
    if (order.recvorder_InvoiceNo) {
      fetchOrderByInvoiceNo(order.recvorder_InvoiceNo);
    }
    const customerKey = order.customer_ID || order.Customer_ID || order.Cust_Id;
    if (customerKey) {
      fetchCustomerDetails(customerKey);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedOrder(null);
    setCustomerDetails(null);
    setDetailedOrder(null);
    setDetailsLoading(false);
    // Restore body scroll when modal is closed
    document.body.style.overflow = 'unset';
  };

  const handleRowClick = (order) => {
    if (order.recvorder_InvoiceNo) {
      navigate(`/Order/orderDetails/${order.recvorder_InvoiceNo}`);
    }
  };

  const statusStyles = {
    Ready: { border: "border-info", text: "text-info", label: "Ready" },
    "In Process": {
      border: "border-warning",
      text: "text-warning",
      label: "In Process",
    },
    "Pre Order": {
      border: "border-secondary",
      text: "text-secondary",
      label: "Pre Order",
    },
    Refund: { border: "border-danger", text: "text-danger", label: "Refund" },
    "Full Refund": { border: "border-danger", text: "text-danger", label: "Full Refund" },
    "Partial Refund": {
      border: "border-warning",
      text: "text-warning",
      label: "Partial Refund",
    },
    Pending: {
      border: "border-warning",
      text: "text-warning",
      label: "Pending",
    },
    Served: { border: "border-success", text: "text-success", label: "Served" },
    Rejected: {
      border: "border-danger",
      text: "text-danger", label: "Rejected",
    },
    Completed: {
      border: "border-success",
      text: "text-success",
      label: "Completed",
    },
    "On the Way": {
      border: "border-primary",
      text: "text-primary",
      label: "On the Way",
    },
    Cancelled: {
      border: "border-danger",
      text: "text-danger",
      label: "Cancelled",
    },
    default: {
      border: "border-secondary",
      text: "text-secondary",
      label: "Not Paid",
    },
  };

  const getOrderRowStatusStyle = (raw) => {
    const s = typeof raw === "string" ? raw.trim() : "";
    if (isFullRefundStatus(s)) {
      return statusStyles["Full Refund"];
    }
    if (isPartialRefundStatus(s)) {
      return statusStyles["Partial Refund"];
    }
    return statusStyles[s] || statusStyles.default;
  };

  const paymentStatusStyles = {
    Paid: {
      border: "border-success",
      text: "text-success",
      label: "Paid",
    },
    "Partial Refund": {
      border: "border-warning",
      text: "text-warning",
      label: "Partial Refund",
    },
    "Full Refund": {
      border: "border-danger",
      text: "text-danger",
      label: "Full Refund",
    },
    "Un-Paid": {
      border: "border-danger",
      text: "text-danger",
      label: "Unpaid",
    },
    default: {
      border: "border-secondary",
      text: "text-secondary",
      label: "Unpaid",
    },
  };

  const paymentMethodStyles = {
    Card: {
      border: "border-info",
      text: "text-info",
      label: "Card",
    },
    Cash: {
      border: "border-primary",
      text: "text-primary",
      label: "Cash",
    },
    default: {
      border: "border-secondary",
      text: "text-secondary",
      label: "N/A",
    },
  };

  const getDisplayPaymentMethod = (orderLike) => {
    const rawMethod = String(orderLike?.recvorder_Paymentmethod || "").trim();
    const rawStatus = String(orderLike?.recvorder_Paymentrecvstatus || "").trim().toLowerCase();
    const rawPaymentBy = String(orderLike?.recvorder_PaymentBy || "").trim().toLowerCase();
    if (
      rawMethod.toLowerCase() === "online" &&
      rawStatus === "completed" &&
      rawPaymentBy === "dojo"
    ) {
      return "Card";
    }
    return rawMethod;
  };

  const handleDateRangeChange = (update) => {
    const [newStartDate, newEndDate] = update;
    if (
      (!newStartDate || moment(newStartDate).isValid()) &&
      (!newEndDate || moment(newEndDate).isValid())
    ) {
      dispatch(
        setFilterField({
          pageKey,
          field: "dateRange",
          value: [newStartDate, newEndDate],
        })
      );
    } else {
      showErrorToast("Invalid date selected");
    }
  }

  return (
    <ErrorBoundary>
      <div>
        <div className="d-flex align-items-center mb-3">
          <div>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <a href="#/">Home</a>
              </li>
              <li className="breadcrumb-item active">Manage Order</li>
            </ol>
            <h1 className="page-header mb-0">Manage Order</h1>
          </div>
        </div>

        <div className="mb-3 mt-2">
          {!initialLoadComplete && <GridSectionLoader loading minHeight="400px" />}

          {showGridToolbar && (
            <ModernGridToolbar
              isDarkMode={isDarkMode}
              title="Orders"
              totalCount={totalRecords}
              searchValue={search}
              onSearchChange={setSearch}
              searchPlaceholder="Search by invoice, customer, table..."
              bodyFlush
              topFilters={
                <>
                  <ModernGridToolbarFilterPill iconClass="fa fa-calendar" wide>
                    <DateRangePicker
                      startDate={
                        startDate && moment(startDate).isValid() ? startDate : null
                      }
                      endDate={endDate && moment(endDate).isValid() ? endDate : null}
                      onChange={handleDateRangeChange}
                      placeholder="Select Date Range"
                    />
                  </ModernGridToolbarFilterPill>
                  <ModernGridToolbarFilterPill
                    iconClass="fa fa-shopping-bag"
                    className="modern-grid-toolbar__filter-pill--status"
                  >
                    <Select
                      value={type}
                      onChange={(val) =>
                        dispatch(setFilterField({ pageKey, field: "type", value: val }))
                      }
                      options={typeOptions}
                      styles={filterPillSelectStyles}
                      placeholder="All Types"
                      isSearchable={false}
                      {...defaultSelectPortalProps}
                    />
                  </ModernGridToolbarFilterPill>
                  <ModernGridToolbarFilterPill
                    iconClass="fa fa-clock"
                    className="modern-grid-toolbar__filter-pill--status"
                  >
                    <Select
                      value={OrderStatus}
                      onChange={(val) =>
                        dispatch(
                          setFilterField({
                            pageKey,
                            field: "OrderStatus",
                            value: val,
                          })
                        )
                      }
                      options={OrderStatusOptions}
                      styles={filterPillSelectStyles}
                      placeholder="All Status"
                      isSearchable={false}
                      {...defaultSelectPortalProps}
                    />
                  </ModernGridToolbarFilterPill>
                  <ModernGridToolbarFilterPill
                    iconClass="fa fa-credit-card"
                    className="modern-grid-toolbar__filter-pill--status"
                  >
                    <Select
                      value={paymentStatus}
                      onChange={(val) =>
                        dispatch(
                          setFilterField({
                            pageKey,
                            field: "paymentStatus",
                            value: val,
                          })
                        )
                      }
                      options={paymentStatusOptions}
                      styles={filterPillSelectStyles}
                      placeholder="All Payments"
                      isSearchable={false}
                      {...defaultSelectPortalProps}
                    />
                  </ModernGridToolbarFilterPill>
                  {renderBulkSelectionActions()}
                </>
              }
              headerControls={
                <ColumnSelection
                  visibleColumns={visibleColumns}
                  setVisibleColumns={(cols) =>
                    dispatch(setVisibleColumns({ pageKey, columns: cols }))
                  }
                  columnOrder={[
                    "frontorderid",
                    "orderType",
                    "orderStatus",
                    "orderSendBy",
                    "invoiceNo",
                    "source",
                    "tillname",
                    "customer",
                    "orderDate",
                    "paymentStatus",
                    "paymentMethod",
                    "totalAmount",
                    "fillBy",
                  ]}
                />
              }
            >
              <div className="px-3 pb-3">

              <GridSectionLoader loading={loading} minHeight="280px">
                {renderNoDataMessage()}

                {orders.length > 0 && (
                  <div
                    className="table-responsive table-container"
                    style={{
                      minHeight: "200px",
                      maxHeight: "450px",
                      overflowY: "auto",
                      overflowX: "auto",
                      position: "relative",
                      zIndex: 1,
                    }}
                  >
                <table className="table table-hover" style={{ position: "relative", zIndex: 1 }}>
                  <thead
                    className="bg-light"
                    style={{
                      position: "sticky",
                      top: 0,
                      zIndex: 5,
                      backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                      color: isDarkMode ? "#ffffff" : "#000000",
                    }}
                  >
                    <tr>
                      {SHOW_BULK_ACTIONS && (
                        <th className="pt-2 pb-2">
                          <input
                            type="checkbox"
                            onChange={handleSelectAll}
                            checked={
                              orders.length > 0 &&
                              selectedOrders.size === orders.length
                            }
                          />
                        </th>
                      )}
                      {visibleColumns.frontorderid && (
                        <th className="pt-2 pb-2">Front Order ID</th>
                      )}
                      {visibleColumns.orderType && (
                        <th className="pt-2 pb-2">Order Type</th>
                      )}
                      {visibleColumns.orderStatus && (
                        <th className="pt-2 pb-2">Order Status</th>
                      )}
                      {visibleColumns.orderSendBy && (
                        <th className="pt-2 pb-2">Order Send By</th>
                      )}
                      {visibleColumns.invoiceNo && (
                        <th className="pt-2 pb-2">Invoice No</th>
                      )}
                      {visibleColumns.source && (
                        <th className="pt-2 pb-2">Source</th>
                      )}
                      {visibleColumns.tillname && (
                        <th className="pt-2 pb-2">Till Name</th>
                      )}
                      {visibleColumns.customer && (
                        <th className="pt-2 pb-2">Customer</th>
                      )}
                      {visibleColumns.orderDate && (
                        <th className="pt-2 pb-2">Order Date</th>
                      )}
                      {visibleColumns.paymentStatus && (
                        <th className="pt-2 pb-2">Payment Status</th>
                      )}
                      {visibleColumns.paymentMethod && (
                        <th className="pt-2 pb-2">Payment Method</th>
                      )}
                      {visibleColumns.totalAmount && (
                        <th className="pt-2 pb-2">Total Amount</th>
                      )}
                      <th className="pt-2 pb-2 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                      {orders.map((order, index) => (
                        <tr
                          key={order.orderID}
                          onClick={() => handleRowClick(order)}
                          style={{ cursor: "pointer" }}
                        >
                          {SHOW_BULK_ACTIONS && (
                            <td className="align-middle">
                              <input
                                type="checkbox"
                                checked={selectedOrders.has(order.recvorder_ID)}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  handleSelectOrder(
                                    order.recvorder_ID,
                                    index,
                                    e
                                  );
                                }}
                                data-chkbox-shiftsel="type1"
                              />
                            </td>
                          )}
                          {visibleColumns.frontorderid && (
                            <td className="align-middle">
                              {order.recvorder_FrontOrderID || "N/A"}
                            </td>
                          )}
                          {visibleColumns.orderType && (
                            <td className="align-middle">
                              {order.recvorder_Ordertype === 'Dine-In' && order.t_Name
                                ? `${order.recvorder_Ordertype} (${order.t_Name})`
                                : order.recvorder_Ordertype}
                            </td>
                          )}
                          {visibleColumns.orderStatus && (
                            <td className="align-middle">
                              {(() => {
                                const status =
                                  getOrderRowStatusStyle(order.recvorder_status);
                                return (
                                  <span
                                    className={`badge border ${status.border} ${status.text} px-2 pt-5px pb-5px rounded fs-12px d-inline-flex align-items-center`}
                                  >
                                    <i className="fa fa-circle fs-9px fa-fw me-5px"></i>{" "}
                                    {order.recvorder_status || status.label}
                                  </span>
                                );
                              })()}
                            </td>
                          )}
                          {visibleColumns.orderSendBy && (
                            <td className="align-middle">
                              {order.sendByName || "N/A"}
                            </td>
                          )}
                          {visibleColumns.invoiceNo && (
                            <td className="align-middle">
                              {order.recvorder_InvoiceNo}
                            </td>
                          )}
                          {visibleColumns.source && (
                            <td className="align-middle">
                              {order.recvorder_ReceivedFrom}
                            </td>
                          )}
                          {visibleColumns.tillname && (
                            <td className="align-middle">
                              {order.tillname || "N/A"}
                            </td>
                          )}
                          {visibleColumns.customer && (
                            <td className="align-middle">
                              {order.customerName || "N/A"}
                            </td>
                          )}
                          {visibleColumns.orderDate && (
                            <td className="align-middle">
                              {order.recvorder_datetime
                                ? moment(order.recvorder_datetime).isValid()
                                  ? moment(order.recvorder_datetime).format(
                                    "DD MMM YYYY : HH:mm"
                                  )
                                  : "N/A"
                                : "N/A"}
                            </td>
                          )}
                          {visibleColumns.paymentStatus && (
                            <td className="align-middle">
                              {(() => {
                                const paymentStatusValue = (order.recvorder_Paymentrecvstatus?.trim() || "").toLowerCase().replace(/[\s_-]/g, "");
                                const displayValue =
                                  paymentStatusValue === "completed"
                                    ? "Paid"
                                    : paymentStatusValue === "partialrefund" || paymentStatusValue === "partial"
                                      ? "Partial Refund"
                                      : paymentStatusValue === "fullrefund" || paymentStatusValue === "refund" || paymentStatusValue === "refunded"
                                        ? "Full Refund"
                                        : "Unpaid";
                                const status = paymentStatusStyles[displayValue] || paymentStatusStyles.default;
                                return (
                                  <span
                                    className={`badge border ${status.border} ${status.text} px-2 pt-5px pb-5px rounded fs-12px d-inline-flex align-items-center`}
                                  >
                                    <i className="fa fa-circle fs-9px fa-fw me-5px"></i>{" "}
                                    {displayValue}
                                  </span>
                                );
                              })()}
                            </td>
                          )}
                          {visibleColumns.paymentMethod && (
                            <td className="align-middle">
                              {(() => {
                                const paymentMethodValue = getDisplayPaymentMethod(order);
                                const status =
                                  paymentMethodStyles[
                                  paymentMethodValue.toLowerCase() === "card"
                                    ? "Card"
                                    : paymentMethodValue.toLowerCase() === "cash"
                                      ? "Cash"
                                      : "default"
                                  ];
                                return (
                                  <span
                                    className={`badge border ${status.border} ${status.text} px-2 pt-5px pb-5px rounded fs-12px d-inline-flex align-items-center`}
                                  >
                                    <i className="fa fa-circle fs-9px fa-fw me-5px"></i>{" "}
                                    {paymentMethodValue || status.label}
                                  </span>
                                );
                              })()}
                            </td>
                          )}
                          {visibleColumns.totalAmount && (
                            <td className="align-middle">
                              {getCurrencySymbol(compCurrency)}
                              {(order.recvorder_Totalamount || 0).toFixed(2)}
                            </td>
                          )}
                          <td className="align-middle text-center">
                            <div onClick={(e) => e.stopPropagation()}>
                              <TableActionsMenu
                                dropup
                                menuEnd
                                menuStyle={{ zIndex: 1050, right: 0, left: "auto" }}
                              >
                                <TableActionsItem
                                  icon="fa fa-eye"
                                  onClick={() => handleOpenModal(order)}
                                >
                                  View Details
                                </TableActionsItem>
                                {/* <Link
                                  to={`/Order/orderDetails/${order.recvorder_InvoiceNo}`}
                                  className="dropdown-item"
                                >
                                  <i className="fa fa-edit me-2"></i>
                                  Edit
                                </Link> */}
                                <TableActionsItem
                                  danger
                                  icon="fa fa-trash"
                                  onClick={() => handleDelete(order)}
                                >
                                  Delete
                                </TableActionsItem>
                              </TableActionsMenu>
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
                  </div>
                )}

                <ModernGridPagerFooter
                  pageSize={pageSize}
                  onPageSizeChange={handlePageSizeChange}
                  totalRecords={totalRecords}
                  pageNumber={pageNumber}
                  totalPages={totalPages}
                  setPageNumber={setPageNumber}
                />
              </GridSectionLoader>
              </div>
            </ModernGridToolbar>
          )}
        </div>

        <OrderDetailsModal
          open={showModal}
          order={selectedOrder}
          detailedOrder={detailedOrder}
          customerDetails={customerDetails}
          loading={detailsLoading}
          currency={compCurrency}
          isDarkMode={isDarkMode}
          getDisplayPaymentMethod={getDisplayPaymentMethod}
          isFullRefundStatus={isFullRefundStatus}
          isPartialRefundStatus={isPartialRefundStatus}
          onClose={handleCloseModal}
        />
      </div>
    </ErrorBoundary>
  );
};

export default ManageOrder;